/**
 * Portal OTP verification (`/verify-otp`).
 *
 * Step 2 of the domain-based MSP child-account login flow. The user lands
 * here from `/login` after submitting their domain — backend has dispatched
 * a 6-digit code to the domain administrator's email. Submission posts to
 * `/msp/domain-login/verify-otp` which sets the auth cookie on success;
 * we then navigate to `/portal`.
 *
 * Mirrors partner-side verify-otp.tsx but adapted for dashboard-portal idioms
 * (raw fetch + @ddmarc/ui primitives + Forest token scope).
 */
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import {
  AlertCircle,
  ArrowLeft,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { Button, Input } from "@ddmarc/ui";
import { useBranding } from "@/lib/branding-context";
import { CardV2 } from "@/components/primitives";
import { PortalBrand } from "@/components/portal-brand";

const searchSchema = z.object({
  domain: z.string().optional(),
  expires_in: z.coerce.number().optional(),
});

export const Route = createFileRoute("/verify-otp")({
  validateSearch: searchSchema,
  component: VerifyOtpPage,
});

function VerifyOtpPage() {
  const { domain, expires_in } = Route.useSearch();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(expires_in || 15 * 60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer — pure cosmetic; backend enforces real expiry.
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const submitCode = async (code: string) => {
    if (!domain) {
      setErrorMessage("Invalid verification session. Please try again.");
      return;
    }
    if (code.length !== 6) {
      setErrorMessage("Please enter all 6 digits");
      return;
    }
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const response = await fetch(
        `/api/v1/msp/domain-login/verify-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ otp_code: code, domain, portal_host: window.location.hostname }),
          credentials: "include",
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Invalid verification code");
      }
      navigate({ to: "/portal" });
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Invalid verification code. Please try again."
      );
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setErrorMessage(null);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (value && index === 5 && newOtp.every((d) => d !== "")) {
      submitCode(newOtp.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) newOtp[i] = pastedData[i] || "";
      setOtp(newOtp);
      const lastIndex = Math.min(pastedData.length, 5);
      inputRefs.current[lastIndex]?.focus();
      if (pastedData.length === 6) submitCode(pastedData);
    }
  };

  if (!domain) {
    return (
      <div
        data-ui="v2"
        className="flex min-h-screen items-center justify-center bg-background p-4"
      >
        <CardV2
          className="w-full max-w-md"
          title={
            <span className="text-center text-[16px] font-semibold">
              Invalid session
            </span>
          }
          subtitle={
            <span className="block text-center">
              This verification page requires a valid domain login request.
            </span>
          }
        >
          <Button asChild className="w-full">
            <Link to="/login">Back to login</Link>
          </Button>
        </CardV2>
      </div>
    );
  }

  return (
    <div
      data-ui="v2"
      className="flex min-h-screen flex-col items-center justify-center bg-background p-4"
    >
      <div className="mb-6 flex flex-col items-center gap-3">
        <PortalBrand branding={branding} size="lg" />
      </div>

      <CardV2
        className="w-full max-w-md"
        title={
          <span className="text-center text-[16px] font-semibold">
            Enter verification code
          </span>
        }
        subtitle={
          <span className="block space-y-1 text-center">
            <span className="block">
              Enter the 6-digit code sent to the domain administrator
            </span>
            {timeLeft > 0 ? (
              <span className="block text-[11.5px]">
                Code expires in{" "}
                <span className="font-mono-data">{formatTime(timeLeft)}</span>
              </span>
            ) : (
              <span className="block text-[11.5px] text-[var(--v2-danger)]">
                Code expired
              </span>
            )}
          </span>
        }
      >
        <div className="space-y-3.5">
          <div
            role="group"
            aria-label="Verification code"
            className="flex justify-center gap-2"
          >
            {otp.map((digit, index) => (
              <Input
                key={index}
                ref={(el: HTMLInputElement | null) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                aria-label={`Digit ${index + 1} of ${otp.length}`}
                autoComplete={index === 0 ? "one-time-code" : "off"}
                aria-invalid={errorMessage ? true : undefined}
                aria-describedby={errorMessage ? "otp-error" : undefined}
                className="h-14 w-12 text-center text-[20px] font-bold tnum"
                disabled={isSubmitting || timeLeft <= 0}
                autoFocus={index === 0}
              />
            ))}
          </div>

          {errorMessage && (
            <div
              id="otp-error"
              role="alert"
              className="flex items-center gap-2 rounded-md border border-[var(--v2-danger)]/40 bg-[var(--v2-danger-soft)] p-2.5 text-[12.5px] text-[var(--v2-danger)]"
            >
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              <p>{errorMessage}</p>
            </div>
          )}

          <Button
            onClick={() => submitCode(otp.join(""))}
            className="w-full"
            disabled={isSubmitting || otp.some((d) => !d) || timeLeft <= 0}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify code"
            )}
          </Button>

          <div className="text-center text-[12px] text-muted-foreground">
            Didn't receive the code?{" "}
            <Link to="/login" className="text-[var(--color-primary)] hover:underline">
              Request a new one
            </Link>
          </div>

          <Button variant="ghost" size="sm" asChild className="w-full">
            <Link to="/login">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to domain login
            </Link>
          </Button>

          <p className="flex items-center justify-center gap-1.5 text-[11.5px] text-[var(--v2-fg-faint)]">
            <ShieldCheck className="h-3 w-3" />
            Secured with one-time codes
          </p>
        </div>
      </CardV2>
    </div>
  );
}
